import classNames from 'classnames';
import {ComponentChildren} from 'preact';
import {useEffect, useRef, useState} from 'preact/hooks';

/**
 * A box which animates its height when its children change.
 * The `isCollapsed` prop determines whether the box is collapsed or not.
 * If the box is collapsed, the children will be hidden and the box will have a height of 0.
 * If the box is not collapsed, the children will be visible and the box will have a height equal to the height of the children.
 *
 * The default animation is 300ms ease-in-out, but this can be overridden by passing a `className` prop.
 * The `fadeOpacity` prop determines whether the box should fade in and out when it is collapsed.
 */
export default function AnimatedCollapsingBox({
  children,
  className,
  fadeOpacity = true,
  isCollapsed,
}: AnimatedCollapsingBoxProps) {
  const childrenContainer = useRef<HTMLDivElement>(null);
  const [childHeight, setChildHeight] = useState(isCollapsed ? 0 : 10000);

  useEffect(() => {
    if (childrenContainer.current) {
      setChildHeight(childrenContainer.current.clientHeight);
    }
  }, [children]);

  return (
    <div
      className={classNames(
        'overflow-hidden transition-all',
        className ?? 'duration-300 ease-in-out',
        {
          'opacity-1': !fadeOpacity || (!isCollapsed && children),
          'opacity-0': fadeOpacity && (isCollapsed || !children),
        }
      )}
      style={{maxHeight: isCollapsed ? '0' : `${childHeight}px`}}
    >
      <div ref={childrenContainer}>{children}</div>
    </div>
  );
}

export type AnimatedCollapsingBoxProps = {
  children: ComponentChildren;
  className?: string;
  fadeOpacity?: boolean;
  isCollapsed: boolean;
};
